import React, { useCallback, useMemo } from "react";
import { ExpandedState, OnChangeFn } from "@tanstack/react-table";
import { CheckDetailResponse } from "@common/schemas/domains/accounting/check-detail-report.schema";
import { CheckDetailView } from "@common/types/views/checkDetailView";
import { View } from "@common/types/views/views";
import { ViewConfig } from "@lib/views";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { useGlobalTransactionViewDrawer } from "@components/transactions/TransactionViewDrawer/useGlobalTransactionViewDrawer";
import { buildCheckDetailRows } from "@common/helpers/domains/accounting/checkDetailRows";
import {
    CheckDetailGroupBy,
    CheckDetailViewRow,
} from "@common/types/reports/check-detail";
import styles from "./CheckDetailTable.module.scss";

interface Props {
    data: CheckDetailResponse;
    groupBy?: CheckDetailGroupBy;
    expanded: ExpandedState;
    onExpandedChange: OnChangeFn<ExpandedState>;
    view: CheckDetailView;
    onViewChange?: (view: View) => void;
    config: ViewConfig<CheckDetailViewRow, string, never>;
}

const getRowClassName = (row: CheckDetailViewRow) =>
    row.rowKind === "check" ? styles.checkRow : undefined;

export const CheckDetailTable: React.FC<Props> = ({
    data,
    groupBy = "bankAccount",
    expanded,
    onExpandedChange,
    view,
    onViewChange,
    config,
}) => {
    const openGlobalTransactionViewDrawer = useGlobalTransactionViewDrawer();

    const rows = useMemo(
        () => buildCheckDetailRows(data, groupBy),
        [data, groupBy],
    );

    const handleRowClick = useCallback(
        (row?: CheckDetailViewRow) => {
            if (row?.rowKind !== "check") {
                return;
            }
            const { transactionId, entityId } = row.check;
            openGlobalTransactionViewDrawer(
                { transactionIds: [transactionId], entityIds: [entityId] },
                {
                    autoSelectFirst: true,
                    initialSelectedTransactionId: transactionId,
                },
            );
        },
        [openGlobalTransactionViewDrawer],
    );

    return (
        <ViewTable
            data={rows}
            view={view}
            config={config}
            onViewChange={onViewChange}
            className={styles.checkDetailTable}
            getRowClassName={getRowClassName}
            onRowClick={handleRowClick}
            emptyView={() => (
                <EmptyState header="No checks or deposits for this selection" />
            )}
            rowBorders={false}
            enableColumnResizing
            fixedTopOffsetPx
            expanded={expanded}
            onExpandedChange={onExpandedChange}
            nestingIndentPx={16}
        />
    );
};
